/**
 * Helpers de Supabase para "Correcciones BIM".
 *
 * Tablas: `bim_entregas`, `bim_comentarios`
 * Bucket: `bim-models`
 *
 * Las RLS policies aseguran que:
 *  - Alumno solo ve sus propias entregas y comentarios.
 *  - Docente/admin ve y edita todo.
 *  - Storage: lectura para autenticados, escritura solo docente/admin.
 *
 * Ningún helper de acá lanza errores no-supabase: siempre devuelve algo o
 * un objeto `{ error }` para que la UI decida cómo mostrarlo.
 */

import { supabase } from './supabase';
import type {
  BimEntrega,
  BimComentario,
  ComentariosOverride,
  EstadosOverride,
  BimEntregaStatus,
} from '../types/bim';
import type { Profile, Course } from '../types/database';
import { calculateFinalAverage, entregaNumToEvalField } from './grades';

const BUCKET = 'bim-models';

// ─── Cursos ──────────────────────────────────────────────────────────

/** Lista cursos activos (RLS restringe lo que el docente puede ver). */
export async function listCursos(): Promise<Course[]> {
  const { data, error } = await supabase
    .from('courses')
    .select('*')
    .in('status', ['active', 'completed'])
    .order('name', { ascending: true });
  if (error) {
    console.error('[bim] listCursos error:', error);
    return [];
  }
  return (data ?? []) as Course[];
}

/**
 * Lista los alumnos inscritos en un curso (status='active') que tengan
 * role='alumno' y estén activos. Usa course_enrollments con join a profiles.
 */
export async function listAlumnosOfCurso(cursoId: string): Promise<Profile[]> {
  const { data, error } = await supabase
    .from('course_enrollments')
    .select('alumno_id, profiles!inner(id, email, full_name, role, avatar_url, active, created_at, updated_at)')
    .eq('course_id', cursoId)
    .eq('status', 'active')
    .eq('profiles.role', 'alumno')
    .eq('profiles.active', true);

  if (error) {
    console.error('[bim] listAlumnosOfCurso error:', error);
    return [];
  }
  // Supabase devuelve `profiles` como array por el !inner — extraemos el primer (único) elemento
  const profs: Profile[] = (data ?? [])
    .map((row: any) => Array.isArray(row.profiles) ? row.profiles[0] : row.profiles)
    .filter(Boolean) as Profile[];
  // Ordenamos por nombre porque el join no soporta orderBy en la tabla joineada en todos los casos
  profs.sort((a, b) => (a.full_name || a.email).localeCompare(b.full_name || b.email));
  return profs;
}

// ─── Listas ──────────────────────────────────────────────────────────

/**
 * Trae las entregas del alumno autenticado, ordenadas por entrega_num desc.
 * RLS filtra por `alumno_id = auth.uid()` automáticamente.
 *
 * Solo devuelve entregas con status='corregido' (publicadas) — las que el
 * docente todavía está revisando (`en_revision`) o están sin armar
 * (`pendiente`) NO se muestran al alumno hasta que el docente publique.
 */
export async function listMyEntregas(): Promise<BimEntrega[]> {
  const { data, error } = await supabase
    .from('bim_entregas')
    .select('*')
    .eq('status', 'corregido')
    .order('entrega_num', { ascending: false });
  if (error) {
    console.error('[bim] listMyEntregas error:', error);
    return [];
  }
  return (data ?? []) as BimEntrega[];
}

/** Trae las entregas de un alumno puntual (uso docente/admin). */
export async function listEntregasOfAlumno(alumnoId: string): Promise<BimEntrega[]> {
  const { data, error } = await supabase
    .from('bim_entregas')
    .select('*')
    .eq('alumno_id', alumnoId)
    .order('entrega_num', { ascending: true });
  if (error) {
    console.error('[bim] listEntregasOfAlumno error:', error);
    return [];
  }
  return (data ?? []) as BimEntrega[];
}

/** Lista de alumnos activos para que el docente elija con quién trabajar. */
export async function listAlumnos(): Promise<Profile[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('role', 'alumno')
    .eq('active', true)
    .order('full_name', { ascending: true });
  if (error) {
    console.error('[bim] listAlumnos error:', error);
    return [];
  }
  return (data ?? []) as Profile[];
}

// ─── Carga de una entrega completa ───────────────────────────────────

/** Devuelve los comentarios + estados de una entrega como mapas planos. */
export async function loadComentariosOfEntrega(entregaId: string): Promise<{
  comentariosOverride: ComentariosOverride;
  estadosOverride:     EstadosOverride;
}> {
  const { data, error } = await supabase
    .from('bim_comentarios')
    .select('item_id, comment_text, estado')
    .eq('entrega_id', entregaId);

  if (error) {
    console.error('[bim] loadComentariosOfEntrega error:', error);
    return { comentariosOverride: {}, estadosOverride: {} };
  }

  const comentariosOverride: ComentariosOverride = {};
  const estadosOverride:     EstadosOverride     = {};
  for (const row of data ?? []) {
    if (row.comment_text) comentariosOverride[row.item_id] = row.comment_text;
    if (row.estado)       estadosOverride[row.item_id]     = row.estado as any;
  }
  return { comentariosOverride, estadosOverride };
}

/** Descarga el data.json de una entrega desde Storage y lo parsea. */
export async function downloadEntregaJSON(dataPath: string): Promise<unknown | null> {
  // Intentamos primero como bucket público (lectura directa via URL).
  // Si fallara (bucket cambió a privado), caemos a download autenticado.
  const { data: publicUrl } = supabase.storage.from(BUCKET).getPublicUrl(dataPath);
  if (publicUrl?.publicUrl) {
    try {
      const res = await fetch(publicUrl.publicUrl + '?v=' + Date.now(), { cache: 'no-cache' });
      if (res.ok) return await res.json();
    } catch (e) {
      console.warn('[bim] public URL falló, intento download autenticado:', e);
    }
  }
  const { data, error } = await supabase.storage.from(BUCKET).download(dataPath);
  if (error || !data) {
    console.error('[bim] downloadEntregaJSON error:', error);
    return null;
  }
  try {
    const text = await data.text();
    return JSON.parse(text);
  } catch (e) {
    console.error('[bim] no se pudo parsear data.json:', e);
    return null;
  }
}

// ─── Mutaciones (solo docente/admin por RLS) ─────────────────────────

interface CreateEntregaArgs {
  alumno_id:      string;
  curso_id?:      string | null;
  entrega_num:    number;          // 1..4
  titulo?:        string;
  archivo_nombre: string;
  jsonBlob:       Blob;            // contenido del data.json
}

/**
 * Sube el data.json al bucket y crea la fila en bim_entregas.
 * Devuelve la entrega creada o `null` + log si algo falla.
 */
export async function createEntregaWithUpload(
  args: CreateEntregaArgs,
): Promise<BimEntrega | null> {
  const { alumno_id, curso_id, entrega_num, titulo, archivo_nombre, jsonBlob } = args;
  const ts   = Date.now();
  const path = `${alumno_id}/entrega-${entrega_num}-${ts}.json`;

  // 1) upload al bucket
  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(path, jsonBlob, {
      cacheControl: '3600',
      upsert: false,
      contentType: 'application/json',
    });
  if (upErr) {
    console.error('[bim] upload data.json error:', upErr);
    return null;
  }

  // 2) insert en bim_entregas
  const { data: { user } } = await supabase.auth.getUser();
  const uploaded_by = user?.id ?? null;

  const { data, error } = await supabase
    .from('bim_entregas')
    .insert({
      alumno_id,
      curso_id: curso_id ?? null,
      entrega_num,
      titulo:         titulo ?? `Entrega ${entrega_num}`,
      archivo_nombre,
      data_path:      path,
      status:         'en_revision',
      uploaded_by,
    })
    .select('*')
    .single();

  if (error || !data) {
    console.error('[bim] insert bim_entregas error:', error);
    // Best effort: limpiar el archivo huérfano del bucket
    await supabase.storage.from(BUCKET).remove([path]).catch(() => {});
    return null;
  }
  return data as BimEntrega;
}

/**
 * Reemplaza el data.json de una entrega existente (subir nueva versión
 * sin borrar la fila ni los comentarios ya hechos).
 */
export async function replaceEntregaJSON(
  entrega: BimEntrega,
  newBlob: Blob,
  archivo_nombre: string,
): Promise<BimEntrega | null> {
  const ts   = Date.now();
  const path = `${entrega.alumno_id}/entrega-${entrega.entrega_num}-${ts}.json`;

  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(path, newBlob, {
      cacheControl: '3600',
      upsert: false,
      contentType: 'application/json',
    });
  if (upErr) {
    console.error('[bim] replace upload error:', upErr);
    return null;
  }

  const { data, error } = await supabase
    .from('bim_entregas')
    .update({ data_path: path, archivo_nombre })
    .eq('id', entrega.id)
    .select('*')
    .single();

  if (error || !data) {
    console.error('[bim] replace update error:', error);
    return null;
  }
  // Borrar el archivo viejo (best-effort, no crítico si falla)
  if (entrega.data_path) {
    await supabase.storage.from(BUCKET).remove([entrega.data_path]).catch(() => {});
  }
  return data as BimEntrega;
}

/** Upsert de un comentario / estado por (entrega_id, item_id). */
export async function upsertComentario(args: {
  entrega_id:    string;
  item_id:       string;
  comment_text?: string | null;
  estado?:       BimComentario['estado'] | null;
}): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  const author_id = user?.id ?? null;

  // Construimos el payload solo con los campos provistos para no pisar
  // el otro al editar uno solo (ej. cambiar estado sin tocar texto).
  const payload: Record<string, unknown> = {
    entrega_id: args.entrega_id,
    item_id:    args.item_id,
    author_id,
  };
  if ('comment_text' in args) payload.comment_text = args.comment_text ?? null;
  if ('estado'       in args) payload.estado       = args.estado       ?? null;

  const { error } = await supabase
    .from('bim_comentarios')
    .upsert(payload, { onConflict: 'entrega_id,item_id' });

  if (error) {
    console.error('[bim] upsertComentario error:', error);
    return false;
  }
  return true;
}

/** Cambia status de una entrega (publicar / despublicar / corregido). */
export async function updateEntregaStatus(
  entregaId: string,
  status: BimEntregaStatus,
): Promise<boolean> {
  const { error } = await supabase
    .from('bim_entregas')
    .update({ status })
    .eq('id', entregaId);
  if (error) {
    console.error('[bim] updateEntregaStatus error:', error);
    return false;
  }
  return true;
}

/** Set / clear de la nota final (1.0..7.0). */
export async function updateEntregaNota(
  entregaId: string,
  nota: number | null,
): Promise<boolean> {
  const { error } = await supabase
    .from('bim_entregas')
    .update({ nota_final: nota })
    .eq('id', entregaId);
  if (error) {
    console.error('[bim] updateEntregaNota error:', error);
    return false;
  }
  return true;
}

/** Set / clear del comentario general del docente sobre la entrega. */
export async function updateEntregaComentarioGeneral(
  entregaId: string,
  texto: string | null,
): Promise<boolean> {
  const { error } = await supabase
    .from('bim_entregas')
    .update({ comentario_general: texto && texto.trim() ? texto : null })
    .eq('id', entregaId);
  if (error) {
    console.error('[bim] updateEntregaComentarioGeneral error:', error);
    return false;
  }
  return true;
}

/**
 * Sincroniza la nota de una entrega BIM al campo correspondiente en
 * `course_evaluations`. Se llama cuando el docente publica/re-publica una
 * entrega con nota desde el dashboard BIM.
 *
 * Mapeo:
 *   Entrega 1 → evaluation_1
 *   Entrega 2 → evaluation_2
 *   Entrega 3 → evaluation_3
 *   Entrega 4 → final_evaluation
 *
 * Comportamiento "last-write-wins":
 *   - Si NO hay row en course_evaluations para (curso, alumno) → se crea.
 *   - Si HAY → se actualiza solo el campo correspondiente y se recalcula
 *     final_average para que quede consistente.
 *   - El docente puede sobreescribir manualmente esto desde el modal de
 *     "Evaluaciones del Curso" — la próxima publicación BIM lo volverá a
 *     pisar (el evento más reciente gana).
 *
 * Si la entrega no tiene curso_id (entregas viejas pre-feature), no hay
 * a dónde sincronizar y devuelve `false`.
 */
export async function syncEntregaToEvaluation(
  cursoId:    string | null,
  alumnoId:   string,
  entregaNum: number,
  nota:       number | null,
): Promise<boolean> {
  if (!cursoId) {
    console.warn('[bim] entrega sin curso_id, no se sincroniza con course_evaluations');
    return false;
  }
  const field = entregaNumToEvalField(entregaNum);
  if (!field) {
    console.warn('[bim] entrega_num inválido para sync:', entregaNum);
    return false;
  }

  // 1) Buscar row existente
  const { data: existing, error: selErr } = await supabase
    .from('course_evaluations')
    .select('*')
    .eq('course_id', cursoId)
    .eq('alumno_id', alumnoId)
    .maybeSingle();

  if (selErr) {
    console.error('[bim] sync select error:', selErr);
    return false;
  }

  if (!existing) {
    // 2a) No existe → crear con esa nota
    const newEval: any = {
      course_id:        cursoId,
      alumno_id:        alumnoId,
      modules_average:  0,
      [field]:          nota,
    };
    newEval.final_average = calculateFinalAverage(newEval);
    const { error: insErr } = await supabase
      .from('course_evaluations')
      .insert(newEval);
    if (insErr) {
      console.error('[bim] sync insert error:', insErr);
      return false;
    }
    return true;
  }

  // 2b) Existe → actualizar campo + recalcular promedio
  const merged: any = { ...existing, [field]: nota };
  const newFinalAvg = calculateFinalAverage(merged);
  const { error: updErr } = await supabase
    .from('course_evaluations')
    .update({ [field]: nota, final_average: newFinalAvg })
    .eq('id', existing.id);
  if (updErr) {
    console.error('[bim] sync update error:', updErr);
    return false;
  }
  return true;
}

/**
 * Borrar una entrega completa.
 *  1. Borra el archivo `data.json` del bucket `bim-models` (best-effort).
 *  2. Borra la fila en `bim_entregas` — el ON DELETE CASCADE de la FK en
 *     `bim_comentarios` se encarga de borrar todos los comentarios y
 *     estados asociados a esa entrega.
 *
 *  RLS: requiere que la policy `bim_entregas_delete` permita docente o admin.
 *  El bucket también requiere policy de delete para escribir/borrar archivos.
 */
export async function deleteEntrega(entrega: BimEntrega): Promise<boolean> {
  // 1) Borrar archivo del bucket — si falla (no existe, permisos, etc.)
  // seguimos igual: lo importante es borrar la fila de la base.
  if (entrega.data_path) {
    const { error: stErr } = await supabase.storage.from(BUCKET).remove([entrega.data_path]);
    if (stErr) console.warn('[bim] no se pudo borrar el archivo del bucket:', stErr.message);
  }

  // 2) Borrar la entrega — cascade borra bim_comentarios
  const { error } = await supabase
    .from('bim_entregas')
    .delete()
    .eq('id', entrega.id);

  if (error) {
    console.error('[bim] deleteEntrega error:', error);
    return false;
  }
  return true;
}
